import {
  Typenames,
  V2VOptionsEnabled,
  V2VOptionsResult,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QueryV2VOptions from 'services/graphql/queries/MoveMoney/V2VOptions';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

interface V2VOptions {
  v2vOptions: V2VOptionsResult;
}

const useV2AOptions = () => {
  const router = useRouter();
  const [isFirstTime, setIsFirstTime] = useState<boolean | undefined>(
    undefined,
  );
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'sender',
  });
  const [{ data, error: gqlError, fetching }] = useQuery<V2VOptions>({
    query: QueryV2VOptions,
    requestPolicy: 'network-only',
  });

  const error =
    gqlError?.graphQLErrors?.find((err) => err?.message) ?? gqlError;

  const v2vOptions = useMemo(
    () => data?.v2vOptions as V2VOptionsResult,
    [data],
  );

  const isV2AEnabled = v2vOptions?.__typename === Typenames.V2VOptionsEnabled;
  const isV2ALimitReached =
    v2vOptions?.__typename === Typenames.V2VOptionsLimitReached;
  const isV2ABlocked = v2vOptions?.__typename === Typenames.V2VOptionsBlocked;

  useEffect(() => {
    if (v2vOptions && isFirstTime === undefined) {
      setIsFirstTime(
        isV2AEnabled && (v2vOptions as V2VOptionsEnabled).isFirstTime,
      );
    }
  }, [isFirstTime, isV2AEnabled, v2vOptions]);

  const { defaultTitle, defaultMessage, cancelButtonTitle, doneButtonTitle } =
    useDefaultErrorStrings();

  const errorModel = useMemo(() => {
    const baseError = {
      screenType: ErrorScreenType.Error,
      messageHeadline: defaultTitle,
      messageBody: defaultMessage,
      primaryButton: {
        title: error?.message ? cancelButtonTitle : doneButtonTitle,
        onPress: () => router.back(),
      },
    };

    if (isV2ABlocked || isV2ALimitReached) {
      const messageDetails = isV2ALimitReached
        ? {
            title: v2vOptions?.title,
            message: v2vOptions?.message,
            eventKey: 'v2aLimitReached',
          }
        : {
            title: translatedText('v2aDisabledTitle'),
            message: translatedText('v2aDisableMessage'),
            eventKey: 'v2aNotAvailable',
          };

      return {
        ...baseError,
        messageHeadline: messageDetails.title || defaultTitle,
        messageBody: messageDetails.message || defaultMessage,
        screenAnalyticsEventKey: messageDetails.eventKey,
      };
    }

    if (error?.message) {
      return {
        ...baseError,
        messageBody: error.message,
      };
    }

    return !isV2AEnabled ? baseError : undefined;
  }, [
    cancelButtonTitle,
    defaultMessage,
    defaultTitle,
    doneButtonTitle,
    error,
    isV2ABlocked,
    isV2AEnabled,
    isV2ALimitReached,
    translatedText,
    v2vOptions,
    router,
  ]);

  return {
    v2vOptions,
    errorModel,
    fetching,
    isFirstTime,
    isV2ADisabled: !isV2AEnabled,
    isV2ABlocked,
  };
};

export default useV2AOptions;
